/**
 * V70 — 게임별 오픈 기간 라우트 Guard
 *
 * 상태별 처리:
 *   before/disabled → "접근 불가" 화면 (게임센터로 복귀 버튼)
 *   after           → "종료됨" 안내 + 리더보드 보기 버튼
 *   during          → 자식 렌더
 *
 * Phase 2 mock: 서버 응답(getSettings) 기반. Phase 3 BE: callable 재검증.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Clock, Trophy, ArrowLeft } from 'lucide-react';
import { computeGamePeriod, periodBadge, formatDiff, type GamePeriodResult } from './gamePeriod';

export interface OpenPeriodGuardProps {
  gameLabel: string;
  loadStatus: () => Promise<{ enabled: boolean; openStartAt: number | null; openEndAt: number | null }>;
  leaderboardPath?: string;
  /**
   * V70-I — 기간 전(before)·후(after)에도 자식 렌더 (엔글 타자왕 상시 연습용).
   *   비공개(disabled = 게임대회 OFF)는 기존대로 차단. 공식 플레이 기간 제한은 자식 화면과 서버가 담당.
   */
  allowOutsidePeriod?: boolean;
  children: React.ReactNode;
}

export function OpenPeriodGuard(p: OpenPeriodGuardProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<GamePeriodResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await p.loadStatus();
        if (cancelled) return;
        setPeriod(computeGamePeriod({ enabled: s.enabled, openStartAt: s.openStartAt, openEndAt: s.openEndAt }));
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || '상태 조회 실패');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [p]);

  if (loading) {
    return <div style={{ padding: 40, color: '#94a3b8', textAlign: 'center' }}>로드 중…</div>;
  }
  if (error) {
    return <div style={{ padding: 40, color: '#f87171', textAlign: 'center' }}>{error}</div>;
  }
  if (!period || period.state === 'during') {
    return <>{p.children}</>;
  }
  if (p.allowOutsidePeriod && (period.state === 'before' || period.state === 'after')) {
    return <>{p.children}</>;
  }

  const badge = periodBadge(period.state);
  const icon = period.state === 'before' ? <Clock size={28} /> : period.state === 'after' ? <Trophy size={28} /> : <Lock size={28} />;

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #020617 0%, #0f172a 100%)', padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#0b1220', border: '1px solid #1e293b', borderRadius: 16, padding: 36, maxWidth: 480, textAlign: 'center', color: '#e2e8f0' }}>
        <div style={{ color: badge.fg, background: badge.bg, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 800, letterSpacing: 1, marginBottom: 16 }}>
          {icon} {badge.label}
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 800, margin: '12px 0 10px', color: '#f1f5f9' }}>{p.gameLabel}</h2>
        <div style={{ color: '#cbd5e1', fontSize: 14, lineHeight: 1.7 }}>
          {period.reason}
          {period.state === 'before' && period.diffMs != null && (
            <div style={{ marginTop: 8, color: '#67e8f9', fontSize: 13 }}>오픈까지 {formatDiff(period.diffMs)}</div>
          )}
          {period.state === 'after' && (
            <div style={{ marginTop: 8, color: '#94a3b8', fontSize: 13 }}>공식 최종 순위는 리더보드에서 확인 가능합니다.</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20, flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/tournament/games')}
            style={{ padding: '10px 16px', background: 'transparent', border: '1px solid #334155', borderRadius: 8, color: '#cbd5e1', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <ArrowLeft size={14} /> 게임센터
          </button>
          {period.state === 'after' && p.leaderboardPath && (
            <button
              onClick={() => navigate(p.leaderboardPath!)}
              style={{ padding: '10px 16px', background: 'linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)', color: '#042f3b', border: 0, borderRadius: 8, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <Trophy size={14} /> 최종 리더보드
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
